<template>
  <v-card>
    <v-card-title>{{ title }}</v-card-title>
    <v-card-text>
      <v-tabs height="40" v-model="tabIndex">
        <v-tab>Básico</v-tab>
        <v-tab>Traducciones <RestaurantTierChip :tier="1" class="ml-1" /></v-tab>
        <v-tab v-if="hasBranches">Sucursales</v-tab>
        <v-tab>Impresión</v-tab>
      </v-tabs>
      <v-tabs-items v-model="tabIndex" class="mt-6">
        <v-tab-item eager>
          <v-form @submit.prevent v-model="valid">
            <v-text-field
              :label="$t('name')"
              v-model="category.name"
              :rules="$rule('required|min3|max128')"
              outlined
              dense
            ></v-text-field>
            <v-text-field
              :label="$t('description')"
              persistent-hint
              :hint="$t('optional')"
              :rules="$rule('max255')"
              v-model="category.description"
              outlined
              dense
            ></v-text-field>
            <div>
              <v-switch
                v-model="category.posOnly"
                label="Mostrar sólo en Punto de Venta"
                dense
                hide-details
              ></v-switch>
              <span class="grey--text">Si activas esta opción, esta categoría no se mostrará en el menú de la app de comensales.</span>
            </div>
          </v-form @submit.prevent>
        </v-tab-item>
        <v-tab-item>
          <template v-if="!restaurantLanguages">
            <p class="grey--text">
              Si quiere traducir su menú a otros idiomas, asegúrese de elegir
              los idiomas en Menú → Ajustes del menú.
            </p>
          </template>
          <template v-else-if="!translationsByLanguage">
            <v-skeleton-loader type="paragraph" />
          </template>
          <template v-else>
            <v-chip
                :color="translationsStatus.color"
                :dark="translationsStatus.dark"
                class="mb-2"
                small
              >
              <v-icon class="mr-1" small
              :class="translationsStatus.iconClass">{{ translationsStatus.icon }}</v-icon>
              <span>{{ translationsStatus.text }}</span>
            </v-chip>
            <p class="grey--text">
              Los idiomas que no tengan traducción se mostrarán en el idioma
              original.<br/>
              <strong>La traducción automática puede tardar hasta una hora.</strong>
            </p>
            <v-form @submit.prevent v-model="translationsValid">
              <div class="translations">
                <CategoryTranslation
                  v-for="lang of restaurantLanguages"
                  :key="lang.id"
                  :language="lang"
                  @toggle="toggleTranslations(lang.id)"
                  :translation="translationsByLanguage[lang.id] || null"
                  @change="translationsChanged = true"
                />
              </div>
            </v-form @submit.prevent>
          </template>
        </v-tab-item>
        <v-tab-item class="mt-4" v-if="hasBranches">
          <p class="grey--text">
            Si quiere que esta categoría no se muestre en algunas sucursales,
            desactívelas debajo.
          </p>
          <div class="branches">
            <v-switch
              v-for="branch of activeBranches"
              :key="branch.id"
              :input-value="branchSelected(branch)"
              @change="toggleBranch(branch)"
              hide-details
              :label="branch.name"
              dense
              v-if="hasPermissionInBranch(branch.id)"
            />
          </div>
        </v-tab-item>
        
        <v-tab-item>
          <v-form @submit.prevent v-model="valid">
            <v-checkbox
              v-for="station of printStations"
              :key="station.stationId"
              v-model="category.comandaPrinters"
              :label="`Comandera ${station.name}`"
              :value="station.stationId"
              hide-details
              dense
            ></v-checkbox>
          </v-form @submit.prevent>
        </v-tab-item>
      </v-tabs-items>
    </v-card-text>
    <v-card-actions class="justify-end">
      <v-btn text v-if="category.id" @click="remove" color="red"
        ><v-icon>mdi-delete</v-icon> {{ $t('delete') }}</v-btn
      >
      <v-btn text @click="cancel">{{ $t('cancel') }}</v-btn>
      <v-btn
        color="primary"
        @click="save"
        :disabled="
          loading || !valid || (translationsByLanguage && !translationsValid)
        "
        >{{ $t('save') }}</v-btn
      >
    </v-card-actions>
  </v-card>
</template>

<script lang="ts">
  import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
  import { ICategory } from '../../interfaces/ICategory';
  import CategoriesService from '../../api/services/categories.service';
  import { vueData } from '../../VueData';
  import { AlertType } from '../../interfaces/IAlert';
  import ConfirmModal from './ConfirmModal.vue';
  import TranslationsService, {
    ITranslationData,
  } from '../../api/services/translations.service';
  import { ILanguage } from '../../interfaces/ILanguage';
  import CategoryTranslation from './CategoryTranslation.vue';
  import { IBranch } from '../../interfaces/IBranch';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { useMenuStore } from '@/composition/use-menu';
import RestaurantTierChip from '../partials/RestaurantTierChip.vue';
  @Component({
    components: { CategoryTranslation,RestaurantTierChip },
  })
  export default class NewCategoryModal extends Vue {
    @Prop() category: ICategory;
    @Prop() menuId: number | null;
    @Prop() branch: IBranch;
    @Prop() branches: IBranch[];
    loading = false;
    categoriesService = new CategoriesService();
    translationsService = new TranslationsService();
    title = '';
    valid = false;
    translationsValid = false;
    tabIndex = 0;
    translationsByLanguage: { [languageId: string]: ITranslationData } = null;
    restaurantLanguages: ILanguage[] = null;
    translationsChanged = false;
    restaurantStore = useRestaurantStore();
    menuStore = useMenuStore();

    get printStations() {
      return this.restaurantStore.printStations;
    }

    async created() {
      this.title = !this.category.id
        ? this.$tc('category.new')
        : this.$t('category.editing', { name: this.category.name }).toString();

      // The checkboxes need an array model; default matches the schema ('1').
      if (!Array.isArray(this.category.comandaPrinters)) {
        this.$set(
          this.category,
          'comandaPrinters',
          this.category.id ? [this.category.comandaPrinter ?? 1] : [1]
        );
      }

      this.restaurantLanguages = vueData.restaurantLanguages;
    }

    @Watch('tabIndex')
    async onTabChanged(newIndex) {
      if (newIndex === 1 && !this.translationsByLanguage) {
        await this.loadTranslations();
      }
    }

    async loadTranslations() {
      this.translationsByLanguage = null;

      if (!this.category.id) {
        this.translationsByLanguage = {};
        return;
      }

      const translations = await this.translationsService.getAllByCategory(
        this.category.restaurantId,
        this.category.id
      );

      const translationsByLanguage = {};

      for (const t of translations) {
        let translation = translationsByLanguage[t.languageId];
        if (!translation)
          translation = translationsByLanguage[t.languageId] = {
            id: t.key,
            languageId: t.languageId,
          };

        const key = t.key.split('-')[1];
        switch (key) {
          case 'name':
            translation.name = t.value;
            break;
          case 'desc':
            translation.description = t.value;
            break;
        }
      }

      this.translationsByLanguage = translationsByLanguage;
    }

    get translationsStatus() {
      const status = {
        icon: 'mdi-refresh',
        text: 'Cargando...',
        iconClass: 'tw-animate-spin',
        color: 'grey',
        dark: false,
      };

      if (!this.translationsByLanguage) return status

      if (!this.category.visible) {
        status.text = 'Este item no está visible';
        status.icon = 'mdi-eye-off';
        status.iconClass = '';
        status.color = 'grey';
        status.dark = true;
        return status;
      }

      const restaurantLanguages = vueData.restaurant.languageIds ?? [];

      const allLanguagesUptodate = restaurantLanguages.every(
        (langId) => {
          const translations = this.translationsByLanguage[langId];

          if (!translations || translations.outdated) return false;

          if (this.category.description && !translations.description) return false;

          return true;
        }
      );

      if (!allLanguagesUptodate) {
        status.text = 'Traduciendo cambios...';
        status.color = 'orange';
        status.dark = true;
      } else {
        status.text = 'Traducciones listas';
        status.color = 'green';
        status.dark = true;
        status.icon = 'mdi-check';
        status.iconClass = '';
      }

      return status;
    }

    toggleTranslations(langId: string) {
      if (!this.translationsByLanguage[langId]) {
        Vue.set(this.translationsByLanguage, langId, {
          languageId: langId,
          name: null,
          description: null,
        });
        this.translationsChanged = true;
      } else {
        Vue.delete(this.translationsByLanguage, langId);
        this.translationsChanged = true;
      }
    }

    get activeBranches() {
      return vueData.branches.filter((b) => b.active);
    }

    get hasBranches() {
      return vueData.allBranches && this.activeBranches.length > 1;
    }

    branchSelected(branch: IBranch) {
      const id = branch.id + '';
      return (
        !this.category.hiddenBranchesIds ||
        this.category.hiddenBranchesIds.indexOf(id) === -1
      );
    }

    toggleBranch(branch: IBranch) {
      const id = branch.id + '';
      if (!this.category.hiddenBranchesIds)
        this.category.hiddenBranchesIds = [id];
      else {
        const index = this.category.hiddenBranchesIds.indexOf(id);
        if (index > -1) {
          this.category.hiddenBranchesIds.splice(index, 1);
          if (this.category.hiddenBranchesIds.length === 0)
            this.category.hiddenBranchesIds = null;
        } else this.category.hiddenBranchesIds.push(id);
      }
    }

    hasPermissionInBranch(branchId: number) {
      return (
        vueData.role.allBranches ||
        vueData.role.branches.some((b) => b.id === branchId)
      );
    }

    async save() {
      this.loading = true;
      try {
        let category = !this.category.id
          ? await this.categoriesService.create(this.category)
          : await this.categoriesService.update(this.category);
        vueData.addAlert(this.$tc('category.saved'), AlertType.Success);
        if (!this.category.id)
          this.$gtag.event('create', { event_category: 'category' });

        if (this.translationsChanged) {
          const translations = [];
          for (const langId in this.translationsByLanguage) {
            if (this.translationsByLanguage[langId].id) continue;
            translations.push(this.translationsByLanguage[langId]);
          }
          await this.translationsService.saveCategoryTranslations(
            category.restaurantId,
            category.id,
            translations
          );
        }

        this.$parent.$emit('saved', category);
        this.$emit('close');
      } catch (e) {
        console.error(e);
        vueData.serverError(e, this.$tc('category.errorSaving'));
      } finally {
        this.loading = false;
      }
    }

    cancel() {
      this.$emit('close');
    }

    /**
     * Deleting a category deletes its products too (API P11), so the
     * confirmation says how many, from the loaded catalog when it is this
     * store's.
     */
    deleteMessage(): string {
      const warning = String(this.$t('delete.warning'));
      const items =
        this.menuStore.loadedRestaurantId === this.category.restaurantId
          ? this.menuStore.items
          : null;
      if (!items)
        return `${this.$t('category.deleteAllProducts')} ${warning}`;

      const count = items.filter(
        (item) => item.categoryId === this.category.id
      ).length;
      return count
        ? `${this.$tc('category.deleteProducts', count, { n: count })} ${warning}`
        : warning;
    }

    async remove() {
      this.$modal.show(
        ConfirmModal,
        {
          title: this.$t('delete.title', { name: this.category.name }),
          message: this.deleteMessage(),
        },
        {
          name: 'delete',
          adaptive: true,
          height: 'auto',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
          width: 600,
          clickToClose: false,
        },
        {
          confirm: async () => {
            try {
              await this.categoriesService.delete(this.category);
              this.$modal.hide('delete');
              this.$parent.$emit('deleted');
            } catch (e) {
              console.error(e);
              vueData.serverError(e);
            }
          },
        }
      );
    }
  }
</script>

<style lang="scss" scoped>
  .branches {
    padding: 10px;
    border-radius: 10px;
    background-color: #fafafa;

    .v-input:first-child {
      margin-top: 0;
    }
  }
</style>
